import {
  DeleteOutlined,
  FileExcelOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import {
  Alert,
  Button,
  message,
  Modal,
  Select,
  Space,
  Table,
  Typography,
  Upload,
} from 'antd';
import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { OrderStatusLabelMap } from '../../constants/order';
import { OrderStatus } from '../../interfaces/Order';

const { Text, Title } = Typography;
const { Option } = Select;

interface TrackingCodeData {
  trackingCode: string;
  rowNumber: number;
}

interface BulkTrackingUpdateModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (trackingCodes: string[], status: OrderStatus) => Promise<void>;
  loading?: boolean;
}

const BulkTrackingUpdateModal: React.FC<BulkTrackingUpdateModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  loading = false,
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [trackingCodes, setTrackingCodes] = useState<TrackingCodeData[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<
    OrderStatus | undefined
  >();
  const [parseError, setParseError] = useState<string>('');

  const handleFileUpload: UploadProps['onChange'] = (info) => {
    let newFileList = [...info.fileList];

    // Keep only the latest file
    newFileList = newFileList.slice(-1);

    setFileList(newFileList);
    setParseError('');

    if (newFileList.length > 0) {
      parseExcelFile(newFileList[0]);
    }
  };

  const parseExcelFile = (file: UploadFile) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });

        // Get the first worksheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        // Extract tracking codes (assuming they are in the first column)
        const codes: TrackingCodeData[] = [];
        let foundCodes = 0;

        jsonData.forEach((row: any, index) => {
          if (Array.isArray(row) && row.length > 0) {
            const cellValue = String(row[0]).trim();

            // Skip empty cells and header-like rows
            if (
              cellValue &&
              cellValue.toLowerCase() !== 'tracking code' &&
              cellValue.toLowerCase() !== 'tracking' &&
              cellValue.toLowerCase() !== 'code' &&
              cellValue.toLowerCase() !== 'mã vận đơn' &&
              cellValue.toLowerCase() !== 'mã' &&
              cellValue !== ''
            ) {
              codes.push({
                trackingCode: cellValue,
                rowNumber: index + 1,
              });
              foundCodes++;
            }
          }
        });

        if (foundCodes === 0) {
          setParseError(
            'Không tìm thấy mã vận đơn nào trong file. Vui lòng đảm bảo mã vận đơn ở cột đầu tiên.'
          );
          setTrackingCodes([]);
        } else {
          setTrackingCodes(codes);
          message.success(
            `Đã đọc thành công ${foundCodes} mã vận đơn từ file Excel`
          );
        }
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        setParseError(
          'Lỗi đọc file Excel. Vui lòng đảm bảo đây là file Excel hợp lệ (.xlsx).'
        );
        setTrackingCodes([]);
      }
    };

    if (file.originFileObj) {
      reader.readAsArrayBuffer(file.originFileObj);
    }
  };

  const beforeUpload = (file: File) => {
    const isExcel =
      file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.name.endsWith('.xlsx');

    if (!isExcel) {
      message.error('Vui lòng tải lên file Excel (.xlsx)');
      return false;
    }

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('File phải nhỏ hơn 2MB');
      return false;
    }

    return false; // Prevent automatic upload
  };

  const handleSubmit = async () => {
    if (trackingCodes.length === 0) {
      message.error('Vui lòng tải lên file Excel chứa mã vận đơn');
      return;
    }

    if (!selectedStatus) {
      message.error('Vui lòng chọn trạng thái để cập nhật đơn hàng');
      return;
    }

    try {
      const codes = trackingCodes.map((item) => item.trackingCode);
      await onSubmit(codes, selectedStatus);
      handleCancel();
    } catch (error) {
      console.error('Error updating orders:', error);
    }
  };

  const handleCancel = () => {
    setFileList([]);
    setTrackingCodes([]);
    setSelectedStatus(undefined);
    setParseError('');
    onCancel();
  };

  const removeTrackingCode = (index: number) => {
    const newCodes = trackingCodes.filter((_, i) => i !== index);
    setTrackingCodes(newCodes);
  };

  const columns = [
    {
      title: 'Dòng',
      dataIndex: 'rowNumber',
      key: 'rowNumber',
      width: 60,
    },
    {
      title: 'Mã vận đơn',
      dataIndex: 'trackingCode',
      key: 'trackingCode',
      ellipsis: true,
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 80,
      render: (_: any, record: TrackingCodeData, index: number) => (
        <Button
          type="text"
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => removeTrackingCode(index)}
          danger
        />
      ),
    },
  ];

  const availableStatuses = [
    OrderStatus.PENDING,
    OrderStatus.PRODUCTION_IN_PROGRESS,
    OrderStatus.PROCESSED,
    OrderStatus.SHIPPING,
    OrderStatus.SHIPPED,
    OrderStatus.REFUNDED,
    OrderStatus.CANCELLED,
  ];

  return (
    <Modal
      title={
        <Space>
          <FileExcelOutlined style={{ color: '#52c41a' }} />
          <span>Cập nhật hàng loạt đơn hàng theo mã vận đơn</span>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      width={800}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Hủy
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
          disabled={trackingCodes.length === 0 || !selectedStatus}
        >
          Cập nhật {trackingCodes.length} đơn hàng
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* File Upload Section */}
        <div>
          <Title level={5}>Bước 1: Tải lên file Excel</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            Tải lên file Excel (.xlsx) với mã vận đơn ở cột đầu tiên
          </Text>
          <Space>
            <Upload
              fileList={fileList}
              onChange={handleFileUpload}
              beforeUpload={beforeUpload}
              accept=".xlsx"
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Chọn file Excel</Button>
            </Upload>
            {/* <Button
              icon={<DownloadOutlined />}
              onClick={() =>
                ExcelTemplateService.downloadTrackingCodeTemplate()
              }
              type="dashed"
            >
              Download Template
            </Button> */}
          </Space>

          {parseError && (
            <Alert
              message="Lỗi đọc file"
              description={parseError}
              type="error"
              style={{ marginTop: 8 }}
              showIcon
            />
          )}
        </div>

        {/* Status Selection */}
        {trackingCodes.length > 0 && (
          <div>
            <Title level={5}>Bước 2: Chọn trạng thái đích</Title>
            <Text
              type="secondary"
              style={{ display: 'block', marginBottom: 8 }}
            >
              Chọn trạng thái bạn muốn cập nhật cho tất cả đơn hàng
            </Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Chọn trạng thái đơn hàng"
              value={selectedStatus}
              onChange={setSelectedStatus}
              size="large"
            >
              {availableStatuses.map((status) => (
                <Option key={status} value={status}>
                  {OrderStatusLabelMap[status]}
                </Option>
              ))}
            </Select>
          </div>
        )}

        {/* Preview Section */}
        {trackingCodes.length > 0 && (
          <div>
            <Title level={5}>Bước 3: Xem lại mã vận đơn</Title>
            <Text
              type="secondary"
              style={{ display: 'block', marginBottom: 8 }}
            >
              Tìm thấy {trackingCodes.length} mã vận đơn. Xóa bất kỳ mã nào
              không chính xác trước khi tiếp tục.
            </Text>
            <Table
              columns={columns}
              dataSource={trackingCodes}
              pagination={trackingCodes.length > 10 ? { pageSize: 10 } : false}
              size="small"
              rowKey={(record) => `${record.rowNumber}-${record.trackingCode}`}
              style={{ maxHeight: 300 }}
              scroll={{ y: 250 }}
            />
          </div>
        )}

        {/* Summary */}
        {trackingCodes.length > 0 && selectedStatus && (
          <Alert
            message="Sẵn sàng cập nhật"
            description={
              <span>
                <strong>{trackingCodes.length}</strong> đơn hàng sẽ được cập
                nhật sang trạng thái:{' '}
                <strong>{OrderStatusLabelMap[selectedStatus]}</strong>
              </span>
            }
            type="info"
            showIcon
          />
        )}
      </Space>
    </Modal>
  );
};

export default BulkTrackingUpdateModal;
